import {getGridDateOperators, GridApi} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import {isDate, omit} from 'lodash';
import {getUTCDateTimeForDisplay} from '../../../../utils/utils';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import FieldPopoverGridFilter, {
  FieldFilterGridData,
} from './FieldPopoverGridFilter';
import AurButton from '../../../common/aur-button/AurButton';
import {
  FieldCategoryMappingObj,
  FieldUITypeMappingByValue,
} from '../custom-field/constant';
import {CustomGridColDef} from '../../../common/aur-data-grid-pro/AurDataGridPro';

function isValidDate(date: Date) {
  return isDate(date) && !isNaN(date.getTime());
}

const fieldCategoryDataArray: FieldFilterGridData[] = [
  {
    id: 1,
    label: 'STANDARD_METADATA',
  },
  {
    id: 2,
    label: 'CUSTOM_METADATA',
  },
  {
    id: 3,
    label: 'TAG_FIELD',
  },
  //   {
  //     id: 4,
  //     label: 'EXPORT',
  //   },
  {
    id: 5,
    label: 'STR',
  },
];

const fieldTypeDataArray: FieldFilterGridData[] = Object.keys(
  omit(FieldUITypeMappingByValue, ['Extracted Text', 'Export', 'Keyword'])
).map((value, index) => {
  return {
    id: index++,
    label: value,
  };
});

const getFieldTableColumnConfig = (
  apiRef: React.MutableRefObject<GridApi>,
  currentPage: number,
  itemsPerPage: number,
  handleRowClick: (rowData: any) => void
) => {
  const TABLE_BASE_WIDTH = 1328;

  const ecaFieldColumns: CustomGridColDef[] = [
    {
      field: 'fieldId',
      headerName: 'No.',
      renderHeader: () => <span className="sr-only">No.</span>,
      display: 'flex',
      minWidth: 48,
      flex: 48 / TABLE_BASE_WIDTH,
      filterable: false,
      sortable: false,
      resizable: false,
      valueGetter: (_, row, __) => {
        return (
          (apiRef.current.getRowIndexRelativeToVisibleRows(
            `${row.index}-${row.fieldId}`
          ) ?? 0) +
          (currentPage - 1) * itemsPerPage +
          1
        );
      },
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
    {
      field: 'fieldLabel',
      headerName: t('fieldManagement.fieldTable.headerTitle.fieldName'),
      display: 'flex',
      minWidth: 175,
      flex: 175 / TABLE_BASE_WIDTH,
      resizable: false,
      filterable: true,
    },
    {
      field: 'fieldCategoryLabel',
      headerName: t('fieldManagement.fieldTable.headerTitle.fieldCategory'),
      display: 'flex',
      minWidth: 175,
      flex: 175 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      valueFormatter: (_, row) => {
        return row?.fieldCategoryLabel ?? '';
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'fieldCategoryLabel'}
          apiRef={apiRef}
          columnField={'fieldCategoryLabel'}
          type={'options'}
          valueGetterObj={FieldCategoryMappingObj}
          dataArray={fieldCategoryDataArray}
          inputTabIndex={0}
        />
      ),
    },
    {
      field: 'fieldUIType',
      headerName: t('fieldManagement.fieldTable.headerTitle.fieldType'),
      display: 'flex',
      minWidth: 175,
      flex: 175 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      valueFormatter: (_, row) => {
        return row?.fieldUIType ?? '';
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'fieldUIType'}
          apiRef={apiRef}
          columnField={'fieldUIType'}
          type={'options'}
          dataArray={fieldTypeDataArray}
          inputTabIndex={0}
        />
      ),
    },
    {
      field: 'updatedBy',
      headerName: t('fieldManagement.fieldTable.headerTitle.lastModifiedBy'),
      display: 'flex',
      minWidth: 175,
      flex: 175 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      filterable: true,
    },
    {
      field: 'updatedOn',
      headerName: t('fieldManagement.fieldTable.headerTitle.lastModifiedOn'),
      display: 'flex',
      minWidth: 175,
      flex: 175 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      type: 'date',
      isDateColumn: true,
      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (value, row) => {
        return isValidDate(value)
          ? getUTCDateTimeForDisplay(row.updatedOn)
          : '';
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          key={'updatedOn'}
          apiRef={apiRef}
          columnField={'updatedOn'}
          type={'date'}
          inputTabIndex={0}
        />
      ),
    },
    {
      field: 'createdBy',
      headerName: t('fieldManagement.fieldTable.headerTitle.createdBy'),
      display: 'flex',
      minWidth: 175,
      flex: 175 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      filterable: true,
    },
    {
      field: 'createdOn',
      headerName: t('fieldManagement.fieldTable.headerTitle.createdOn'),
      display: 'flex',
      minWidth: 175,
      flex: 175 / TABLE_BASE_WIDTH,
      sortable: true,
      resizable: false,
      type: 'date',

      filterOperators: getGridDateOperators().map((operator) => ({
        ...operator,
      })),
      isDateColumn: true,
      valueGetter: (value) => {
        return new Date(value);
      },
      valueFormatter: (value, row) => {
        return isValidDate(value)
          ? getUTCDateTimeForDisplay(row.createdOn)
          : '';
      },
      renderHeaderFilter: () => (
        <FieldPopoverGridFilter
          apiRef={apiRef}
          columnField={'createdOn'}
          type={'date'}
          inputTabIndex={0}
        />
      ),
    },
    {
      field: 'arrow',
      headerName: 'arrow',
      renderHeader: () => <span className="sr-only">arrow</span>,
      flex: 48 / TABLE_BASE_WIDTH,
      sortable: false,
      resizable: false,
      filterable: false,
      disableTooltip: true,
      renderCell: (params: any) => {
        return (
          <AurButton
            type="text"
            size="Medium"
            name={t('fieldManagement.viewDetails')}
            ariaLabel={t('fieldManagement.arrowIconLabel')}
            startIcon={
              <AurIcon
                icon={IconNames.ChevronRight}
                color={IconColors.Primary}
              />
            }
            onClick={() => handleRowClick?.(params?.row)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                handleRowClick?.(params?.row);
              }
            }}
          />
        );
      },
      renderHeaderFilter: () => (
        <span className="sr-only">{t('common.noFilter')}</span>
      ),
    },
  ];

  return ecaFieldColumns;
};

export default getFieldTableColumnConfig;
